/* ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§1 */

const TAG_NAME = 'r-sticky-sidebar';
const DEFAULT_MIN_WIDTH = 750;
const MODE_VIEWPORT_PIN = 'viewport-pin';
const COLUMN_HEIGHT_PROP = '--r-sticky-column-height';

class RStickySidebar extends HTMLElement {
  /** Extra px gap kept above a top-pinned column. @type {number} */
  _topSpacing = 0;

  /** Extra px gap kept below a bottom-pinned column. @type {number} */
  _bottomSpacing = 0;

  /** Viewport width (px) below which sticky is disabled. @type {number} */
  _minWidth = DEFAULT_MIN_WIDTH;

  /** True in `mode="viewport-pin"`: no scroll listener, no `top` writes. @type {boolean} */
  _viewportPin = false;

  /** True while the column is taller than the viewport (travel mode). @type {boolean} */
  _tall = false;

  /** Current numeric `top` while in travel mode. @type {number} */
  _top = 0;

  /** Travel bounds while in travel mode. @type {number} */
  _minTop = 0;
  /** @type {number} */
  _maxTop = 0;

  /** Last seen scroll position, for direction deltas. @type {number} */
  _lastScrollY = 0;

  /** requestAnimationFrame id for the deferred first measure. @type {number} */
  _bootId = 0;

  /** requestAnimationFrame id for a coalesced re-measure. @type {number} */
  _rafId = 0;

  /** The inline `top` value last written, to spot external (morph) resets. @type {string} */
  _lastTop = '';

  /** The `--r-sticky-column-height` value last written, same purpose. @type {string} */
  _lastHeight = '';

  /** True once listeners/observers are wired up. @type {boolean} */
  _setupDone = false;

  /** Bound `resize` handler. @type {(() => void) | null} */
  _onResize = null;

  /** Bound `scroll` handler. @type {(() => void) | null} */
  _onScroll = null;

  /** @type {ResizeObserver | null} */
  _resizeObs = null;

  /** @type {MutationObserver | null} */
  _mutationObs = null;

  connectedCallback() {
    if (this._setupDone) return;

    this._viewportPin = this.getAttribute('mode') === MODE_VIEWPORT_PIN;
    this._topSpacing = parseFloat(this.getAttribute('top-spacing') ?? '') || 0;
    this._bottomSpacing = parseFloat(this.getAttribute('bottom-spacing') ?? '') || 0;
    this._minWidth =
      parseFloat(this.getAttribute('min-width') ?? '') || DEFAULT_MIN_WIDTH;

    // Layout is not guaranteed settled on the first connected tick (fonts,
    // images). Defer one frame; observers keep it honest afterwards.
    this._bootId = requestAnimationFrame(() => this._setup());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._bootId);
    cancelAnimationFrame(this._rafId);
    this._rafId = 0;
    if (!this._setupDone) return;

    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    this._resizeObs?.disconnect();
    this._mutationObs?.disconnect();
    this.style.top = '';
    this.style.removeProperty(COLUMN_HEIGHT_PROP);
    this._lastTop = '';
    this._lastHeight = '';
    this._setupDone = false;
  }

  /** Public: force a re-measure (e.g. after dynamic content changes). */
  update() {
    if (this._setupDone) this._schedule();
  }

  _setup() {
    const onResize = () => this._schedule();
    this._onResize = onResize;
    window.addEventListener('resize', onResize, { passive: true });

    // viewport-pin never positions from scroll — that is the whole point of the
    // mode — so the listener is not registered at all.
    if (!this._viewportPin) {
      const onScroll = () => this._travel();
      this._onScroll = onScroll;
      window.addEventListener('scroll', onScroll, { passive: true });
      this._lastScrollY = window.scrollY;
    }

    // The short/tall decision only changes with the column's own height
    // (accordions, variant swaps, fonts) or the viewport height.
    if ('ResizeObserver' in window) {
      this._resizeObs = new ResizeObserver(() => this._schedule());
      this._resizeObs.observe(this);
    }

    // ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§2
    if ('MutationObserver' in window) {
      this._mutationObs = new MutationObserver(() => {
        const stale = this._viewportPin
          ? this.style.getPropertyValue(COLUMN_HEIGHT_PROP) !== this._lastHeight
          : this.style.top !== this._lastTop;
        if (stale) this._schedule();
      });
      this._mutationObs.observe(this, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    // Web fonts can change the column height after first paint.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this._schedule()).catch(() => {});
    }

    this._setupDone = true;
    this._reposition();
  }

  /** Coalesce re-measures into a single rAF. */
  _schedule() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = 0;
      this._reposition();
    });
  }

  /* ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§3 */
  _travel() {
    const y = window.scrollY;
    const dy = y - this._lastScrollY;
    this._lastScrollY = y;

    if (!this._tall || dy === 0) return;

    const top = Math.min(this._maxTop, Math.max(this._minTop, this._top - dy));
    if (top !== this._top) {
      this._top = top;
      this._writeTop(`${top}px`);
    }
  }

  /* ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§4 */
  _reposition() {
    if (!this.isConnected) return;

    // Below the breakpoint the CSS rule that enables sticky is not in effect;
    // make sure no stale offset lingers.
    if (window.innerWidth <= this._minWidth) {
      this._tall = false;
      this._writeTop('');
      this._writeHeight('');
      return;
    }

    // ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§5
    if (this._viewportPin) {
      const height = Math.round(this.offsetHeight);
      this._writeHeight(height > 0 ? `${height}px` : '');
      return;
    }

    const columnHeight = this.offsetHeight;
    const viewportHeight = window.innerHeight;
    const headerOffset =
      parseFloat(getComputedStyle(this).getPropertyValue('--r-sticky-header-offset')) || 0;

    const maxTop = headerOffset + this._topSpacing;
    const minTop = viewportHeight - columnHeight - this._bottomSpacing;

    if (columnHeight > 0 && minTop < maxTop) {
      // ↗ .cursor/references/code-notes/r-sticky-sidebar.js.md#§6
      const wasTall = this._tall;
      this._tall = true;
      this._minTop = minTop;
      this._maxTop = maxTop;

      const seed = wasTall ? this._top : this.getBoundingClientRect().top;
      this._top = Math.min(maxTop, Math.max(minTop, seed));
      this._writeTop(`${this._top}px`);
    } else {
      // Fits the viewport → hand back to the CSS rule's positive `top`.
      this._tall = false;
      this._writeTop('');
    }
  }

  /**
   * Sets the inline `top`, remembering it so the MutationObserver can tell our
   * own write apart from an external (morph) reset.
   * @param {string} value
   */
  _writeTop(value) {
    if (this.style.top !== value) this.style.top = value;
    this._lastTop = value;
  }

  /**
   * Sets `--r-sticky-column-height` (viewport-pin mode), remembering it so the
   * MutationObserver can tell our own write apart from an external (morph)
   * reset. Writing a custom property rather than a layout property is also what
   * keeps the ResizeObserver from feeding back into itself: the value cannot
   * affect this element's own measured height.
   * @param {string} value
   */
  _writeHeight(value) {
    if (this.style.getPropertyValue(COLUMN_HEIGHT_PROP) !== value) {
      if (value) this.style.setProperty(COLUMN_HEIGHT_PROP, value);
      else this.style.removeProperty(COLUMN_HEIGHT_PROP);
    }
    this._lastHeight = value;
  }
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, RStickySidebar);
}
